/**
 * LOGISPRD-12506 — 콘솔 API 에러를 `PanelState<T>` 의 `error` 분기(§ `state/panelState.ts`)로 접는다.
 *
 * 핵심은 401 을 두 갈래로 가르는 것이다(핸드오프 §2.1, `http.ts` 주석 참고):
 *  - 게이트웨이 토큰 실패(진짜 `ERROR_UNAUTHORIZED`)와
 *  - `@PreAuthorize` 거절(권한 부족, 서버 `SecurityConfig` 가 403 이 아니라 401 로 매핑)
 * 이 둘 다 `response.status === 401` 로 온다 — 구분할 다른 신호가 없다. 그래서 여기서는
 * 아예 구분하지 않고 401 을 전부 `forbidden` 으로 본다: 콘솔은 `skipOktaReauth: true` 를 항상
 * 켜서(재인증 루프 방지) 애초에 토큰 갱신에 실패한 요청이 이 자리까지 오지 않는다
 * (요청 인터셉터가 요청 전에 갱신한다) — 남는 401 은 사실상 전부 권한 부족이다.
 *
 * 규약 A(`ApiResponseV2<T>`) 와 규약 B(raw DTO 성공 + `ApiResponseV2` 실패) 는 성공 응답의
 * shape 만 다르고, 실패 응답은 둘 다 같은 `{ meta: { errorCode, message } }` 자리에서 읽힌다
 * (`MusinsaOmsApiCodeException` 은 서버 오류도 400 `ERROR_SYSTEM` 으로 옴) — 그래서 `toPanelError`
 * 는 status 로만 분기하고 body 파싱은 공통 자리 하나로 처리한다.
 */

/** axios 에러(또는 그 형태를 흉내 낸 값)에서 HTTP status 를 읽는다. 응답이 없으면(네트워크 오류 등) undefined. */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }

  const response = (error as { response?: { status?: unknown } }).response
  return typeof response?.status === 'number' ? response.status : undefined
}

/** axios 에러 응답 본문. 응답이 없으면 undefined. */
export const bodyOf = (error: unknown): unknown => {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }

  return (error as { response?: { data?: unknown } }).response?.data
}

const FORBIDDEN_MESSAGE = '이 조치에 권한이 없어요. (MOMS_ENG 또는 MASTER 권한 필요)'

/**
 * Okta 토큰 **갱신 실패**. `request.ts` 의 요청 인터셉터가 `response` 없이 이 code 만 담아 던지므로
 * 상태·본문 분기로는 잡히지 않는다. 요청이 서버에 나가지 않았다는 뜻이기도 하다.
 */
const OKTA_TOKEN_UNAVAILABLE_CODE = 'OKTA_ACCESS_TOKEN_UNAVAILABLE'

const HTTP_GATEWAY_TIMEOUT = 504

export type PanelError = { kind: 'forbidden' | 'other'; message: string }

export const isOktaTokenUnavailable = (error: unknown): boolean => (error as { code?: string } | null)?.code === OKTA_TOKEN_UNAVAILABLE_CODE

/**
 * 서버가 처리했는지 알 수 없는 실패 — 응답 없이 끊겼거나 게이트웨이가 기다리다 끊었다(504).
 * 비멱등 쓰기에서는 같은 요청을 다시 보내게 하지 말고 상태부터 확인하게 해야 한다.
 */
export const isOutcomeUnknown = (error: unknown): boolean => {
  if (isOktaTokenUnavailable(error)) return false
  const status = httpStatusOf(error)
  return status === undefined || status === HTTP_GATEWAY_TIMEOUT
}

/** 게이트가 콘솔 기본(`MOMS_ENG` 또는 `MASTER`)과 다른 API 는 `forbiddenMessage` 로 권한 문구를 좁힌다. */
export const toPanelError = (error: unknown, forbiddenMessage = FORBIDDEN_MESSAGE): PanelError => {
  if (isOktaTokenUnavailable(error)) {
    return { kind: 'other', message: '로그인 세션이 만료됐어요. 어드민 화면에서 다시 로그인한 뒤 이 화면을 새로고침하세요.' }
  }

  const status = httpStatusOf(error)

  if (status === 401) {
    return { kind: 'forbidden', message: forbiddenMessage }
  }

  const body = bodyOf(error) as { meta?: { errorCode?: string; message?: string } } | undefined
  const errorCode = body?.meta?.errorCode
  const message = body?.meta?.message

  if (errorCode) {
    return { kind: 'other', message: message ? `${errorCode} · ${message}` : errorCode }
  }

  return { kind: 'other', message: `요청이 실패했어요 (HTTP ${status ?? '?'})` }
}
